import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw } from "lucide-react";

export default function AdminShipping() {
  const [items, setItems] = useState([]);
  const [remindersSent, setRemindersSent] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/shipping-oversight")
      .then(({ data }) => {
        // New shape: { reminders_sent, orders } — fall back to array for older clients
        if (Array.isArray(data)) setItems(data);
        else { setItems(data.orders || []); setRemindersSent(data.reminders_sent || 0); }
      })
      .catch((err) => console.warn("shipping load failed", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const refund = async (order_id) => {
    if (!window.confirm("Auto-refund this order and send apology email to buyer?")) return;
    try {
      await api.post(`/admin/orders/${order_id}/auto-refund`);
      toast.success("Refund issued + email sent");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed");
    }
  };

  if (loading) return <p className="font-mono-b p-6" data-testid="ship-loading">Loading...</p>;

  const overdue = items.filter((i) => i.overdue);
  const onTrack = items.filter((i) => !i.overdue);

  return (
    <div className="space-y-6" data-testid="admin-shipping">
      <button onClick={load} className="border-2 border-black bg-white px-3 py-1 font-mono-b font-bold uppercase text-xs hover-nudge inline-flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> REFRESH
      </button>
      {remindersSent > 0 && (
        <div className="border-2 border-black bg-[#D4FF00] p-2 font-mono-b text-xs" data-testid="reminders-sent">
          <b>{remindersSent}</b> escrow reminder email(s) sent to sellers whose orders were paid &gt;24h ago without shipping proof.
        </div>
      )}

      <section>
        <h3 className="font-display text-2xl mb-3">OVERDUE <span className="text-[#FF007F]">({overdue.length})</span></h3>
        {overdue.length === 0 ? (
          <p className="font-mono-b p-6 text-center border-2 border-dashed border-black">Nothing overdue. Sellers on time.</p>
        ) : (
          <div className="space-y-3">
            {overdue.map((o) => (
              <div key={o.order_id} className="bg-[#FF007F]/10 border-2 border-black brutal-shadow-sm p-4" data-testid={`ship-overdue-${o.order_id}`}>
                <div className="flex justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-mono-b text-xs text-black/60">{o.order_id}</p>
                    <p className="font-display text-xl">₹{o.total} · {o.user_email}</p>
                    <p className="font-mono-b text-[11px] text-[#FF007F] font-bold flex items-center gap-1 mt-1">
                      <AlertTriangle className="w-3 h-3" /> Deadline was {new Date(o.deadline).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => refund(o.order_id)}
                    data-testid={`ship-refund-${o.order_id}`}
                    className="bg-[#FF007F] text-white border-2 border-black px-3 py-2 font-mono-b font-bold uppercase text-xs hover-nudge h-fit"
                  >
                    AUTO-REFUND + APOLOGY EMAIL
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="font-display text-2xl mb-3">ON TRACK ({onTrack.length})</h3>
        {onTrack.length === 0 ? (
          <p className="font-mono-b p-6 text-center border-2 border-dashed border-black">No paid orders waiting to ship.</p>
        ) : (
          <div className="space-y-2">
            {onTrack.map((o) => (
              <div key={o.order_id} className="bg-white border-2 border-black p-3 flex justify-between flex-wrap gap-2" data-testid={`ship-ontrack-${o.order_id}`}>
                <div>
                  <p className="font-mono-b text-xs text-black/60">{o.order_id}</p>
                  <p className="font-mono-b text-sm">₹{o.total} · {o.user_email}</p>
                </div>
                <p className="font-mono-b text-[11px]">Deadline: {new Date(o.deadline).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
